import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, FileText, Calendar } from "lucide-react";
import { toast } from "sonner";
import { epsteinEvidence } from "../lib/api";
import { KIND_META, CONFIDENCE_META } from "../components/epstein/efConsts";

const EpsteinEvidence = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await epsteinEvidence(slug);
        if (active) setData(r);
      } catch {
        toast.error("Evidence not found.");
        if (active) setData(null);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center font-mono text-[#757065]">// LOADING EVIDENCE…</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex flex-col items-center justify-center gap-3" data-testid="ef-evidence-404">
        <div className="font-mono text-xs text-[#757065]">// EVIDENCE NOT FOUND</div>
        <Link to="/epstein-files" className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">← back to the file</Link>
      </div>
    );
  }

  const { evidence, entities, sources } = data;
  const conf = CONFIDENCE_META[evidence.confidence_level] || null;

  return (
    <div className="min-h-screen bg-[#0F0F0F]" data-testid={`ef-evidence-page-${evidence.id}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10">
        <Link to="/epstein-files" data-testid="ef-evidence-back"
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] mb-6">
          <ArrowLeft size={12} /> Back to evidence board
        </Link>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] border border-[#5DD4B3]/50 px-2 py-0.5">
            <FileText size={9} className="inline mr-1" />
            {(evidence.evidence_type || "evidence").replace(/_/g, " ")}
          </span>
          {evidence.date && (
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#CBB892] border border-[#CBB892]/40 px-2 py-0.5">
              <Calendar size={9} className="inline mr-1" /> {evidence.date}
            </span>
          )}
          {conf && (
            <span style={{ color: conf.color, borderColor: conf.color }}
              className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5">
              {conf.label}
            </span>
          )}
        </div>

        <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3] leading-tight mb-3">{evidence.title}</h1>

        {evidence.short_summary && (
          <p className="text-base text-[#A39E93] leading-relaxed mb-4">{evidence.short_summary}</p>
        )}

        {evidence.legal_note && (
          <div className="border border-[#E0A526]/40 bg-[#1a1408] px-3 py-2 mb-4 text-[12px] italic text-[#CBB892]">
            ⚠ {evidence.legal_note}
          </div>
        )}

        {evidence.thumbnail_url && (
          <div className="border border-[#2D2A26] bg-[#0a0907] mb-4 overflow-hidden">
            <img src={evidence.thumbnail_url} alt={evidence.title} className="w-full max-h-[480px] object-contain" />
          </div>
        )}

        {evidence.excerpt && (
          <blockquote className="border-l-2 border-[#D92D20] pl-3 italic text-[#CBB892] mb-5">
            "{evidence.excerpt}"
          </blockquote>
        )}

        {evidence.full_notes && (
          <div className="border border-[#2D2A26] bg-[#0F0F0F] p-4 mb-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// EDITOR NOTES</div>
            <div className="text-sm text-[#A39E93] leading-relaxed whitespace-pre-wrap">{evidence.full_notes}</div>
          </div>
        )}

        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-6 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[10px]">
          {evidence.publisher_or_origin && (<><div className="text-[#757065]">origin</div><div className="text-[#E4DFD3]">{evidence.publisher_or_origin}</div></>)}
          {evidence.location_label && (<><div className="text-[#757065]">location</div><div className="text-[#E4DFD3]">{evidence.location_label}</div></>)}
          {evidence.page_reference && (<><div className="text-[#757065]">page ref</div><div className="text-[#E4DFD3]">{evidence.page_reference}</div></>)}
          {evidence.file_type && (<><div className="text-[#757065]">file type</div><div className="text-[#E4DFD3]">{evidence.file_type}</div></>)}
          {evidence.source_url && (
            <>
              <div className="text-[#757065]">source url</div>
              <div className="text-[#E4DFD3] break-all">
                <a href={evidence.source_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#D92D20]">
                  {evidence.source_url} <ExternalLink size={9} className="inline" />
                </a>
              </div>
            </>
          )}
        </div>

        <section className="mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// LINKED ENTITIES · {entities.length}</div>
          {entities.length === 0 ? (
            <div className="font-mono text-xs text-[#757065]">None.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {entities.map((e) => {
                const m = KIND_META[e.kind] || KIND_META.reference;
                return (
                  <Link key={e.id} to={`/epstein-files/entity/${e.slug}`}
                    className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 hover:border-[#D92D20]/60 block"
                    data-testid={`ef-evidence-entity-${e.id}`}>
                    <span style={{ color: m.color }} className="font-mono text-[8px] uppercase tracking-[0.25em]">{m.label}</span>
                    <div className="font-heading text-sm text-[#E4DFD3]">{e.name}</div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// SOURCES · {sources.length}</div>
          {sources.length === 0 ? (
            <div className="font-mono text-xs text-[#757065]">None on file.</div>
          ) : (
            <div className="space-y-2">
              {sources.map((s) => (
                <Link key={s.id} to={`/epstein-files/source/${s.id}`}
                  className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 hover:border-[#D92D20]/60 block"
                  data-testid={`ef-evidence-source-${s.id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#5DD4B3]">{(s.source_type || "source").replace(/_/g, " ")}</span>
                    {s.publisher && <span className="font-mono text-[8px] text-[#757065]">{s.publisher}</span>}
                  </div>
                  <div className="font-heading text-sm text-[#E4DFD3]">{s.title}</div>
                  <div className="font-mono text-[10px] text-[#A39E93] break-all">{s.url}</div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <div className="border border-[#2D2A26] bg-[#0a0907] px-3 py-2 mt-8 text-[11px] italic text-[#757065]">
          Inclusion does not imply guilt or criminal conduct. Read each linked source.
        </div>
      </div>
    </div>
  );
};

export default EpsteinEvidence;
